import React from 'react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { T } from '../designTokens';

// ---------------------------------------------------------------------------
// First run: an account with no audits yet. Instead of an empty page under
// the form, say what an audit looks at, in the order the scanner runs it,
// and point to a finished report so the customer knows what they will get.
// ---------------------------------------------------------------------------

const STAGES: Array<{ term: string; detail: string }> = [
  {
    term: 'Consent banner and opt-out links',
    detail: 'Whether the site shows a Do Not Sell or Share link and a banner, and whether saying no is as easy as saying yes.',
  },
  {
    term: 'Trackers',
    detail: 'Every third-party request the site makes, sorted by tracker type, before and after a consent choice.',
  },
  {
    term: 'Global Privacy Control',
    detail: 'A second visit with the GPC opt-out signal on, checking that marketing trackers stop.',
  },
  {
    term: 'Privacy policy',
    detail: 'The disclosures CCPA requires, looked for in the policy text itself.',
  },
];

export default function FirstAuditGuide() {
  const isMobile = useIsMobile();
  const marketingUrl = (process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com').replace(/\/$/, '');

  return (
    <section aria-labelledby="first-audit-heading">
      <h2
        id="first-audit-heading"
        style={{
          margin: '0 0 6px',
          fontFamily: T.fontDisplay,
          fontSize: '20px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: T.gray800,
        }}
      >
        What the audit checks
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '15px', lineHeight: 1.6, color: T.gray600, maxWidth: '62ch' }}>
        Your sites will be listed here after your first audit. Each finding names the CCPA section it rests on
        and shows the evidence behind it.
      </p>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(180px, 220px) minmax(0, 1fr)',
          borderTop: `1px solid ${T.gray200}`,
        }}
      >
        {STAGES.map(({ term, detail }) => (
          <React.Fragment key={term}>
            <dt
              style={{
                padding: isMobile ? '14px 0 2px' : '14px 16px 14px 0',
                borderBottom: isMobile ? 'none' : `1px solid ${T.gray200}`,
                fontSize: '15px',
                fontWeight: 600,
                color: T.gray800,
              }}
            >
              {term}
            </dt>
            <dd
              style={{
                margin: 0,
                padding: isMobile ? '0 0 14px' : '14px 0',
                borderBottom: `1px solid ${T.gray200}`,
                fontSize: '15px',
                lineHeight: 1.55,
                color: T.gray600,
              }}
            >
              {detail}
            </dd>
          </React.Fragment>
        ))}
      </dl>
      <p style={{ margin: '20px 0 0', fontSize: '15px' }}>
        <a
          href={`${marketingUrl}/sample-report`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link"
          style={{ fontWeight: 600, color: T.cobalt600 }}
        >
          See a sample report
        </a>
      </p>
    </section>
  );
}
